import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  MenuItem,
  Box,
  Button,
  Divider,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";
import AlertSnack from "../AlertSnack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "../../../services/axios";
import { nationalitys } from "../../../utils/nacionalitys";
import { DadosCadSchemaValidate } from "./dadosCadSchemaValidate";
import { councilTypeEnum, maritalStatusEnum } from "./dadosCadEnum";
import KeyIcon from "@mui/icons-material/Key";
import ChangePasswordModal from "../ChangePasswordModal";
import server from "../../../functions/server";

function DadosCad({ dados, nome, cnpj }) {
  if (!dados || !dados[0]) {
    return (
      <Typography variant="h6">
        Não foi possível carregar os dados cadastrais.
      </Typography>
    );
  }

  const [loading, setLoading] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [councilTypeValue, setCouncilTypeValue] = useState(
    councilTypeEnum[dados?.[0]?.[8]?.trim?.()] ?? undefined,
  );
  const [maritalStatusValue, setMaritalStatusValue] = useState(
    maritalStatusEnum[dados?.[0]?.[4]?.trim?.()] ?? undefined,
  );
  const [nationalityValue, setNationalityValue] = useState(dados[0][3]);
  let changepass = localStorage.getItem("changepass");
  const [openDialog, setOpenDialog] = useState(
    changepass == "S" ? true : false,
  );
  const [tipoSnack, setTipoSnack] = useState("");
  const [textoSnack, setTextoSnack] = useState("");

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm({
    resolver: zodResolver(DadosCadSchemaValidate),
    defaultValues: {
      email: dados[0][0],
      phone: dados[0][1],
      nationality: dados[0][3],
      maritalStatus: maritalStatusEnum[dados?.[0]?.[4]?.trim?.()] ?? undefined,
      job: dados[0][7],
      councilType: councilTypeEnum[dados?.[0]?.[8]?.trim?.()] ?? undefined,
      councilNumber: dados[0][9] ?? undefined,
    },
  });

  const user = cnpj;

  const handleChangeCouncilType = (event) => {
    setValue("councilType", event.target.value);
    setCouncilTypeValue(event.target.value);
  };

  const handleChangeMaritalStatus = (event) => {
    setValue("maritalStatus", event.target.value);
    setMaritalStatusValue(event.target.value);
  };

  const handleChangeNationality = (event) => {
    setValue("nationality", event.target.value);
    setNationalityValue(event.target.value);
  };

  const openModal = () => {
    setOpenDialog(true);
  };

  const closeModal = () => {
    setOpenDialog(false);
  };

  const handleSubmitEditUserInfo = async (data) => {
    try {
      setLoading(true);

      await api.post("/portalCliente/atualiza_dados", {
        jsonid: localStorage.getItem("token"),
        cnpj: localStorage.getItem("user"),
        email: data.email,
        email_conf: false,
        codparc: dados[0][2],
        telefone: data.phone,
        nacionalidade: data.nationality,
        estado_civil: data.maritalStatus,
        rg: data.stateRegistration,
        profissao: data.job,
        tipo_conselho: data.councilType,
        numero_conselho: data.councilNumber,
        snkId: dados[0][2],
      });

      setLoading(false);
      setOpenSnack(true);
      setTextoSnack("Dados atualizados com sucesso!");
      setTipoSnack("success");
    } catch (error) {
      console.error(error);
      setLoading(false);
      setOpenSnack(true);
      setTextoSnack("Erro ao atualizar os dados. Tente novamente.");
      setTipoSnack("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AlertSnack
        open={openSnack}
        tipo={tipoSnack}
        texto={textoSnack}
        onClose={() => setOpenSnack(0)}
      />

      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600} mb={2}>
            Dados Cadastrais
          </Typography>
          <Typography variant="subtitle2" color="textSecondary">
            <strong>IMPORTANTE:</strong> Preencha os campos abaixo para
            atualizar seus dados cadastrais.
          </Typography>

          <Typography variant="subtitle2" color="textSecondary">
            Isso é fundamental para garantir a melhor experiência para você.
          </Typography>
        </Box>

        <Box>
          <ChangePasswordModal
            open={openDialog}
            onClose={closeModal}
            onSubmit={({ success, message }) => {
              setOpenSnack(1);
              setTipoSnack(success ? "success" : "error");
              setTextoSnack(message);
              setLoading(false);
            }}
            loading={loading}
            server={server}
            user={user}
          />
          <Button
            variant="outlined"
            endIcon={<KeyIcon />}
            size="small"
            onClick={openModal}
          >
            Alterar Senha
          </Button>
        </Box>
      </Box>

      <Divider />

      <Grid container direction="column" justifyContent="space-between" mt={3}>
        <Grid item xs={6}>
          <Typography variant="h5">{nome}</Typography>
        </Grid>

        <Grid
          item
          xs={6}
          sx={{
            mb: 5,
          }}
        >
          <Typography variant="h5">
            {cnpj.length > 11 ? "CNPJ: " : "CPF: "}
            {cnpj.length > 11
              ? cnpj.replace(
                  /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                  "$1.$2.$3/$4-$5",
                )
              : cnpj.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}
          </Typography>
        </Grid>

        <form onSubmit={handleSubmit(handleSubmitEditUserInfo)}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel shrink htmlFor="id" disabled>
                  Código
                </InputLabel>
                <TextField
                  label="Código"
                  id="id"
                  size="small"
                  value={dados[0][2]}
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel
                  shrink
                  htmlFor="email"
                  error={!!errors.email?.message}
                >
                  E-mail
                </InputLabel>
                <TextField
                  error={!!errors.email?.message}
                  label="E-mail"
                  id="email"
                  size="small"
                  type="email"
                  InputLabelProps={{ shrink: true }}
                  {...register("email")}
                />

                {errors.email && (
                  <FormHelperText error>{errors.email?.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel
                  shrink
                  htmlFor="phone"
                  error={!!errors.phone?.message}
                >
                  Telefone
                </InputLabel>
                <TextField
                  error={!!errors.phone?.message}
                  label="Telefone"
                  id="phone"
                  size="small"
                  type="tel"
                  InputLabelProps={{ shrink: true }}
                  {...register("phone")}
                />

                {errors.phone && (
                  <FormHelperText error>{errors.phone?.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel id="nationality-select-label">
                  Nacionalidade
                </InputLabel>
                <Select
                  id="nationality-select-label"
                  label="Nacionalidade"
                  onChange={handleChangeNationality}
                  value={nationalityValue}
                >
                  {nationalitys.map((national) => (
                    <MenuItem value={national}>{national}</MenuItem>
                  ))}
                </Select>

                {errors.nationality && (
                  <FormHelperText error>
                    {errors.nationality?.message === "Required"
                      ? "Campo obrigatório"
                      : errors.nationality?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel id="demo-simple-select-label">
                  Estado Civil
                </InputLabel>
                <Select
                  id="demo-simple-select-label"
                  label="Estado Civil"
                  onChange={handleChangeMaritalStatus}
                  value={maritalStatusValue}
                >
                  <MenuItem value="1">Solteira(o)</MenuItem>
                  <MenuItem value="2">Casada(o)</MenuItem>
                  <MenuItem value="3">Divorciada(o)</MenuItem>
                  <MenuItem value="4">Viúva(o)</MenuItem>
                </Select>

                {errors.maritalStatus && (
                  <FormHelperText error>
                    {errors.maritalStatus?.message === "Required"
                      ? "Campo obrigatório"
                      : errors.maritalStatus?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel shrink htmlFor="adress" disabled>
                  Endereço
                </InputLabel>
                <TextField
                  disabled
                  label="Endereço"
                  id="adress"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={dados[0][5]}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel shrink htmlFor="stateRegistration">
                  {dados[0][6] || cnpj.length > 11
                    ? "Inscrição estadual"
                    : "R.G"}
                </InputLabel>
                <TextField
                  label={
                    dados[0][6] || cnpj.length > 11
                      ? "Inscrição estadual"
                      : "R.G"
                  }
                  id="stateRegistration"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled
                  value={dados[0][6]}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel shrink htmlFor="job" error={!!errors.job?.message}>
                  Profissão
                </InputLabel>
                <TextField
                  error={!!errors.job?.message}
                  label="Profissão"
                  id="job"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  {...register("job")}
                />

                {errors.job && (
                  <FormHelperText error>{errors.job?.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel id="councilType">Tipo do Conselho</InputLabel>
                <Select
                  id="councilType"
                  label="Tipo do Conselho"
                  onChange={handleChangeCouncilType}
                  value={councilTypeValue}
                >
                  <MenuItem value="1">CRM</MenuItem>
                  <MenuItem value="2">CRO</MenuItem>
                  <MenuItem value="3">CREFITO</MenuItem>
                  <MenuItem value="4">COREN</MenuItem>
                  <MenuItem value="5">ESTETICISTA</MenuItem>
                  <MenuItem value="6">CRBM</MenuItem>
                  <MenuItem value="7">CRN</MenuItem>
                  <MenuItem value="8">CRF</MenuItem>
                </Select>

                {errors.councilType && (
                  <FormHelperText error>
                    {errors.councilType?.message === "Required"
                      ? "Campo obrigatório"
                      : errors.councilType?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mb: 2,
                }}
              >
                <InputLabel
                  shrink
                  htmlFor="councilNumber"
                  error={!!errors.councilNumber?.message}
                >
                  Número do Conselho
                </InputLabel>
                <TextField
                  error={!!errors.councilNumber?.message}
                  label="Número do Conselho"
                  id="councilNumber"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  {...register("councilNumber")}
                />

                {errors.councilNumber && (
                  <FormHelperText error>
                    {errors.councilNumber?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end">
            <LoadingButton
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                maxWidth: 200,
                textTransform: "capitalize",
                fontWeight: 600,
              }}
            >
              Salvar
            </LoadingButton>
          </Box>
        </form>
      </Grid>
    </>
  );
}

export default DadosCad;
